import { useRef, useState } from "preact/hooks";
import { useLocation, useRoute } from "preact-iso";
import {
  ArrowUpTrayIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { api } from "../lib/api-client";
import { adminHref } from "../lib/platform-admin";
import { MarkdownEditor, type EditorRef } from "../components/editor";
import { MarkdownPreview } from "../components/markdown-preview";
import { RevisionHistory } from "../components/revision-history";
import { ImageModal } from "../components/image-modal";
import { ValidationErrors } from "../components/validation-errors";
import { DEFAULT_TEMPLATE, applyTemplate } from "../lib/frontmatter-template";
import { useConfirm } from "../lib/use-confirm";
import { useDocumentDraft } from "../lib/use-document-draft";
import {
  Alert,
  Button,
  Card,
  CardBody,
  PageHeader,
  SkeletonText,
} from "../components/ui";

export function ContentEdit() {
  const { params } = useRoute();
  const projectId = params.projectId!;
  const slug = params.slug!;

  const editorRef = useRef<EditorRef | null>(null);
  const { route } = useLocation();
  const { confirm, dialog: confirmDialog } = useConfirm();

  const draft = useDocumentDraft({
    projectId,
    slug,
    onMissing: async (_id, slug, project) => {
      const template = project?.frontmatterTemplate ?? DEFAULT_TEMPLATE;
      return applyTemplate(template, {
        slug,
        publishedAt: new Date().toISOString(),
      });
    },
  });

  const [historyOpen, setHistoryOpen] = useState(false);
  const [imagesOpen, setImagesOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function onDelete() {
    const ok = await confirm({
      title: `/${slug} を削除しますか？`,
      description: "本文・履歴・参照リンクが失われます。",
      confirmLabel: "削除する",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.deleteContent(projectId, slug);
      // SPA navigation, not window.location: useDocumentDraft keeps a
      // beforeunload handler installed while the draft is dirty, so a full page
      // load here asked "leave without saving?" right after a successful
      // delete — and answering "stay" stranded the user editing a document
      // that no longer exists (the next save would recreate it). A route()
      // unmounts ContentEdit, taking the listener with it.
      route(adminHref(`/projects/${projectId}`));
    } catch (e) {
      setDeleteError((e as Error).message);
    }
  }

  async function onSave() {
    const beforeSave = editorRef.current?.getValue();
    const saved = await draft.save();
    if (!saved) return;
    const current = editorRef.current?.getValue();
    if (current === beforeSave && current !== saved.body) {
      editorRef.current?.replaceAll(saved.body);
    }
  }

  return (
    <section class="space-y-6">
      {confirmDialog}
      <PageHeader
        backHref={adminHref(`/projects/${projectId}`)}
        backLabel="プロジェクト"
        title={`/${slug}`}
        monoTitle
        actions={
          <>
            <Button
              variant="secondary"
              iconLeft={<PhotoIcon class="h-4 w-4" />}
              onClick={() => setImagesOpen(true)}
            >
              画像
            </Button>
            <Button
              variant="primary"
              iconLeft={<ArrowUpTrayIcon class="h-4 w-4" />}
              loading={draft.saving}
              disabled={draft.saving || !draft.ready}
              onClick={() => void onSave()}
            >
              {draft.saving ? "保存中…" : "保存"}
            </Button>
          </>
        }
      />

      <ImageModal
        projectIdOrSlug={projectId}
        open={imagesOpen}
        onClose={() => setImagesOpen(false)}
      />

      {draft.loadError && (
        <Alert tone="danger">
          <div class="flex flex-wrap items-center justify-between gap-2">
            <span>本文を読み込めませんでした。{draft.loadError} 再読み込みするまで編集・保存はできません。</span>
            <Button size="sm" variant="secondary" onClick={draft.reload}>
              再読み込み
            </Button>
          </div>
        </Alert>
      )}

      <ValidationErrors errors={draft.saveErrors} variant="panel" />

      <div class="grid gap-4 lg:grid-cols-2">
        <div>
          {!draft.ready ? (
            <div class="h-[60vh] rounded-lg border border-border bg-bg p-4 shadow-xs">
              <SkeletonText lines={14} />
            </div>
          ) : (
            <MarkdownEditor
              initial={draft.draftBody}
              onChange={draft.setDraftBody}
              onReady={(ref) => (editorRef.current = ref)}
            />
          )}
        </div>
        <div class="lg:max-h-[80vh] lg:overflow-auto">
          <MarkdownPreview source={draft.draftBody} />
        </div>
      </div>

      <Card>
        <button
          type="button"
          onClick={() => setHistoryOpen((v) => !v)}
          class="flex w-full items-center justify-between px-5 py-3 text-left text-sm font-medium text-fg"
        >
          <span class="flex items-center gap-2">
            <ClockIcon class="h-4 w-4 text-fg-subtle" />
            更新履歴
          </span>
          <span class="text-xs text-fg-subtle">
            {historyOpen ? "閉じる" : "開く"}
          </span>
        </button>
        {historyOpen && (
          <div class="border-t border-border-subtle p-5">
            <RevisionHistory
              projectId={projectId}
              slug={slug}
              showPreview
              dirty={draft.dirty}
              refreshKey={draft.historyRefreshKey}
              onRestored={(restored) => {
                draft.applyRestoredDocument(restored);
                // CodeMirror is uncontrolled — sync its internal doc to the
                // restored body so the editor reflects the rollback.
                editorRef.current?.replaceAll(restored.body);
              }}
            />
          </div>
        )}
      </Card>

      {/* Destructive action lives at the bottom, away from the save button in
          the header, to avoid accidental deletion. Mirrors the project
          DangerZoneSection pattern. */}
      <div class="space-y-4">
        <h3 class="flex items-center gap-1.5 text-base font-semibold tracking-tight text-danger-fg">
          <ExclamationTriangleIcon class="h-5 w-5" />
          危険な操作
        </h3>
        <Card tone="danger">
          <CardBody>
            <div class="flex flex-wrap items-center justify-between gap-4">
              <div class="space-y-1">
                <div class="text-sm font-medium text-fg">このページを削除</div>
                <p class="text-xs text-fg-subtle">
                  本文・更新履歴・参照リンクがすべて失われます。元に戻せません。
                </p>
              </div>
              <Button
                variant="danger"
                iconLeft={<TrashIcon class="h-4 w-4" />}
                onClick={() => void onDelete()}
              >
                削除…
              </Button>
            </div>
            {deleteError && (
              <Alert tone="danger" class="mt-3">
                {deleteError}
              </Alert>
            )}
          </CardBody>
        </Card>
      </div>
    </section>
  );
}
